import { isEmpty, isNil, sum } from 'lodash';
import { CSSProperties, useCallback, useMemo, useState } from 'react';
import { View } from '@shared/components/View';
import { LoadingIndicator } from '@shared/components/loading';
import { TitledWorkflowTemplate, getEntityTitle } from '@shared/data/entity';
import {
  RelatedEntity,
  RelatedEntityField,
  RelatedItem,
  useRelatedEntities,
} from '@shared/data/entity/fields/RelatedEntityField';
import { useCardNavigationContext } from '@shared/scenes/cards/navigation';
import { useGetChannel } from '@shared/scenes/channel/hooks';
import { WarningSubfooter } from '@shared/scenes/entity/components/WarningSubfooter';
import { FieldRecord } from '@shared/scenes/entity/types';
import { getComponentLabel } from '@shared/scenes/workflows/utils';
import { getAnchorName } from '@shared/styles';
import { API } from '@shared/types/api';
import { useBoolean } from '@shared/util/hooks';
import { i18n } from '@shared/util/i18n';
import { useChannelSlug, useNavigator } from '@shared/util/navigation';
import { notNil } from '@shared/util/notNil';
import { AddMoreButton } from '../AddMoreButton';
import { EditFieldContainer } from '../EditFieldContainer';
import { RelatedEntityList } from './RelatedEntityList';
import { RelatedEntityPicker } from './RelatedEntityPicker';
import { RelatedQuantityList } from './RelatedQuantityList';

interface Props {
  field: RelatedEntityField | null;
  component: API.RelatedCardComponent;
  updateField: (componentId: string, field: RelatedEntityField | null) => void;
  fields: FieldRecord;
  error?: string;
  allowCreate?: boolean;
  allowClear?: boolean;
  entityId?: string;
  style?: CSSProperties;
}

export function RelatedEntityEditField({
  component,
  field,
  updateField,
  fields,
  error,
  allowCreate = true,
  allowClear = true,
  entityId,
  style,
}: Props) {
  const hasQuantity = component.hasQuantity;
  const anchorName = getAnchorName('related-entity', component.id);
  const [isPickerOpen, openPicker, closePicker] = useBoolean(false);
  const items = useMemo(() => field?.items ?? [], [field]);
  const {
    entities,
    workflowTemplate,
    loading: workflowEntitiesLoading,
  } = useRelatedEntities(field, component.workflowTemplateId ?? null);
  const [openingEntity, setOpeningEntity] = useState<RelatedEntity | null>(null);
  const isMisconfigured = !component.workflowTemplateId;

  const onChange = useCallback(
    (updatedEntities: API.WorkflowEntity[]): void => {
      const updatedItems = updatedEntities.map(entity => {
        const match = items.find(item => item.id === entity.id);
        return match ?? { id: entity.id, quantity: hasQuantity ? 1 : undefined };
      });
      updateField(component.id, new RelatedEntityField(updatedItems, updatedEntities));
    },
    [component.id, updateField, items, hasQuantity],
  );

  const updateItemQuantity = useCallback(
    (id: string, quantity: number) => {
      const updatedItems = items.map(item => {
        return item.id === id ? { ...item, quantity } : item;
      });
      updateField(component.id, new RelatedEntityField(updatedItems, entities));
    },
    [component.id, updateField, items, entities],
  );

  const hasEntities = !isEmpty(items) || workflowEntitiesLoading;
  const showEntities = !workflowEntitiesLoading && hasEntities;
  // The edit field is the single source of truth for the max-cards error: it is
  // shown immediately while the selection exceeds the component's limit (email/url
  // pattern, counting selected cards to match the server ARRAY_MAX_SIZE_EXCEEDED
  // check). Because the same `maxExceeded` message also arrives from the server,
  // suppress that stale server error once the selection is back within the limit,
  // while still surfacing any other server/required error via `error`.
  const displayError = useMemo(() => {
    const maxExceededMessage = isNil(component.limit)
      ? undefined
      : i18n('card.form.errors.maxExceeded', { count: component.limit });
    const isOverLimit = !isNil(component.limit) && items.length > component.limit;
    const nonLimitError = error === maxExceededMessage ? undefined : error;
    return nonLimitError ?? (isOverLimit ? maxExceededMessage : undefined);
  }, [component.limit, items.length, error]);
  const label = hasQuantity
    ? getLabelWithCountAndType(component, items)
    : getLabelWithCount(component, entities.length);
  const isReadOnly = !!component.readonly || isMisconfigured;
  const printContent = hasQuantity
    ? getPrintEntitesWithQuantity(entities, items, workflowTemplate)
    : getPrintEntitiesWithoutQuantity(entities, workflowTemplate);
  const openEntity = useOpenRelatedEntity(entityId);
  const showClearButton = hasEntities && !isReadOnly && allowClear;

  const onClear = useCallback(() => {
    onChange([]);
  }, [onChange]);

  const onItemPress = useCallback(
    (entity?: API.WorkflowEntity) => {
      if (isNil(entity)) {
        return;
      }
      setOpeningEntity(entity);
      openEntity(entity);
      setTimeout(() => {
        setOpeningEntity(null);
      }, 500);
    },
    [openEntity],
  );

  const onItemRemove = useCallback(
    (item?: RelatedItem) => {
      if (isNil(item)) {
        return;
      }
      updateField(
        component.id,
        new RelatedEntityField(
          items.filter(e => e.id !== item.id),
          entities.filter(e => e.id !== item.id),
        ),
      );
    },
    [component.id, items, entities, updateField],
  );

  const addMoreLabel = hasEntities
    ? i18n('card.blocks.cardsInput.addMore')
    : i18n('card.blocks.cardsInput.add');

  const isClickable = !isReadOnly && !hasEntities && !workflowEntitiesLoading;

  return (
    <>
      <EditFieldContainer
        component={component}
        anchorName={anchorName}
        label={label}
        showLabel={true}
        showPlaceholder={!hasEntities}
        showClearButton={showClearButton}
        // Cards are held as ids and resolved separately, so a card id that matches no record leaves
        // this field blank however many ids it holds. Reported while resolving as well, so the mark
        // does not blink out and back on a slow lookup.
        hasVisibleValue={workflowEntitiesLoading || !isEmpty(entities)}
        onPress={isClickable ? openPicker : undefined}
        onClear={onClear}
        error={displayError}
        subfooter={
          <WarningSubfooter
            showWarning={isMisconfigured}
            message={i18n('workflowTemplate.component.error.misconfigured')}
          />
        }
        isGrayScale={isMisconfigured}
        footer={
          !isReadOnly && (
            <View row>
              <AddMoreButton component={component} onPress={openPicker} label={addMoreLabel} />
            </View>
          )
        }
        style={style}
        printContent={printContent}
      >
        {workflowEntitiesLoading && <LoadingIndicator style={{ alignItems: 'flex-start' }} />}
        {showEntities && hasQuantity && (
          <RelatedQuantityList
            component={component}
            items={items}
            entities={entities}
            workflowTemplate={workflowTemplate}
            openPicker={openPicker}
            updateItemQuantity={updateItemQuantity}
            onItemPress={isReadOnly ? undefined : onItemPress}
            onItemRemove={isReadOnly ? undefined : onItemRemove}
            openingEntity={openingEntity}
            loading={workflowEntitiesLoading}
          />
        )}
        {showEntities && !hasQuantity && (
          <RelatedEntityList
            items={items}
            entities={entities}
            workflowTemplate={workflowTemplate}
            onItemPress={isReadOnly ? undefined : onItemPress}
            onItemRemove={isReadOnly ? undefined : onItemRemove}
            component={component}
            openingEntity={openingEntity}
            loading={workflowEntitiesLoading}
          />
        )}
      </EditFieldContainer>
      {isPickerOpen && (
        <RelatedEntityPicker
          entities={entities}
          onChange={onChange}
          onClose={closePicker}
          anchorName={anchorName}
          component={component}
          field={field}
          fields={fields}
          canCreateNew={allowCreate}
        />
      )}
    </>
  );
}

function useOpenRelatedEntity(currentEntityId?: string) {
  const currentChannelSlug = useChannelSlug();
  const { navigate } = useNavigator();
  const getChannel = useGetChannel();
  const { addStackItem } = useCardNavigationContext();

  return useCallback(
    async (entity: RelatedEntity) => {
      const { channel } = await getChannel(entity.channelId);

      if (!channel) {
        return;
      }

      if (currentEntityId && currentChannelSlug) {
        addStackItem({
          entityId: entity.id,
          channelSlug: channel.slug,
          backTooltip: i18n('card.navigation.returnToRelatedCard'),
        });
      }

      navigate('CardUpdate', { cardId: entity.id, channelSlug: channel.slug });
    },
    [addStackItem, getChannel, navigate, currentEntityId, currentChannelSlug],
  );
}

function getLabelWithCountAndType(
  component: API.RelatedCardComponent,
  items: RelatedItem[],
): string {
  const count = items.length;
  const totalQuantity = sum(items.map(item => item.quantity ?? 0));
  const componentLabel = component.label ?? '';
  return i18n('card.blocks.cardsInput.quantity.label', {
    label: componentLabel,
    totalCount: totalQuantity,
    count,
  });
}

function getLabelWithCount(component: API.RelatedCardComponent, count: number) {
  if (count > 0) {
    const componentLabel = getComponentLabel(component);
    return `${componentLabel} (${count})`.trim();
  } else {
    return undefined;
  }
}

function getPrintEntitesWithQuantity(
  entities: RelatedEntity[],
  items: RelatedItem[],
  workflowTemplate?: TitledWorkflowTemplate,
) {
  return entities
    .map(entity => {
      const item = items.find(i => i.id === entity.id);
      return `${getEntityTitle(entity, workflowTemplate)} (${item?.quantity ?? 0})`;
    })
    .filter(notNil)
    .join('\n');
}

function getPrintEntitiesWithoutQuantity(
  entities: RelatedEntity[],
  workflowTemplate?: TitledWorkflowTemplate,
) {
  return entities
    .map(entity => getEntityTitle(entity, workflowTemplate))
    .filter(notNil)
    .join(', ');
}
